import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import './ConfirmDialog.css';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose?: (...args: any[]) => any;
  onCancel?: (...args: any[]) => any;
  onConfirm: ((...args: any[]) => any) | null;
  title?: string;
  message?: string | React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  variant?: 'default' | 'danger' | 'warning';
  type?: string;
}

const ConfirmDialog = ({
  isOpen,
  onClose,
  onCancel,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
  variant = 'default',
}: ConfirmDialogProps) => {
  if (!isOpen) return null;

  const closeHandler = onClose || onCancel;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      closeHandler?.();
    }
  };

  const handleConfirm = () => {
    if (!loading) {
      onConfirm?.();
    }
  };

  const handleCancel = () => {
    if (!loading) {
      closeHandler?.();
    }
  };

  return (
    <div className="confirm-dialog-overlay" onClick={handleOverlayClick}>
      <div className="confirm-dialog">
        <div className="confirm-dialog-header">
          <h3>{title}</h3>
          <button
            className="confirm-dialog-close"
            onClick={handleCancel}
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        <div className="confirm-dialog-content">
          {typeof message === 'string' ? <p>{message}</p> : message}
        </div>

        <div className="confirm-dialog-buttons">
          <button
            className={`confirm-dialog-confirm-btn confirm-dialog-${variant}`}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Loading...' : confirmText}
          </button>
          <button
            className="confirm-dialog-cancel-btn"
            onClick={handleCancel}
            disabled={loading}
          >
            {cancelText}
          </button>
        </div>
      </div>
    </div>
  );
};

ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  loading: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'danger', 'warning']),
};

export default ConfirmDialog;
